import * as React from 'react'
import { AlertCircle, Mail, Trash2, UserCheck, UserPlus, UserX } from 'lucide-react'
import {
  useSuperAdmins,
  useInviteSuperAdmin,
  useDeactivateSuperAdmin,
  useDeleteInvitedSuperAdmin,
  useReactivateSuperAdmin,
  useResendSuperAdminInvite,
} from '@/hooks/useSuperAdmins'
import { useAuth } from '@/auth/AuthContext'
import { requestErrorMessage } from '@/helpers/apiClient'
import { inviteByEmailInputSchema } from '@lib/input/user.input'
import type { UserDto } from '@lib/dto/user.dto'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { FormActions } from '@/components/ui/form-actions'
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableActionsHead,
  TableActionsCell,
  TableEmptyRow,
} from '@/components/ui/table'
import { RowActions, type RowAction } from '@/components/ui/row-actions'
import { LoadingState, ErrorState } from '@/components/ui/states'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import { UserStatusBadge } from '@/components/UserStatusBadge'

/** Inline invite form for a new platform super admin. */
function InviteForm({ onDone }: { onDone: () => void }) {
  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const invite = useInviteSuperAdmin()

  function save(): void {
    setError(null)
    const parsed = inviteByEmailInputSchema.safeParse({ name, email })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check the fields and try again.')
      return
    }
    invite.mutate(parsed.data, { onSuccess: onDone, onError: (e) => setError(requestErrorMessage(e)) })
  }

  return (
    <Card className="space-y-4 p-4">
      <Field label="Name" htmlFor="sa-invite-name">
        <Input
          id="sa-invite-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Full name"
          autoFocus
        />
      </Field>
      <Field label="Email" htmlFor="sa-invite-email">
        <Input
          id="sa-invite-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
        />
      </Field>
      {error ? <FormError>{error}</FormError> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={save}
          disabled={name.trim() === '' || email.trim() === ''}
          pending={invite.isPending}
        >
          {invite.isPending ? 'Sending…' : 'Send invite'}
        </Button>
      </FormActions>
    </Card>
  )
}

function SuperAdminRow({ user }: { user: UserDto }) {
  const { user: me } = useAuth()
  const deactivate = useDeactivateSuperAdmin()
  const deleteInvited = useDeleteInvitedSuperAdmin()
  const reactivate = useReactivateSuperAdmin()
  const resend = useResendSuperAdminInvite()
  const [resent, setResent] = React.useState(false)
  const isSelf = me?.id === user.id


  /**
   * The action that ends an account. An invitation nobody accepted is deleted —
   * there is nothing to keep, and holding the address would stop the invite being
   * re-sent to a corrected one. An account in use is deactivated, reversibly.
   */
  /**
   * The one action that ends an account: an unaccepted invitation is deleted,
   * since there is nothing to keep and holding the address would stop it being
   * re-sent to a corrected one; an account in use is deactivated, reversibly.
   */
  function lifecycleAction(): RowAction {
    if (user.status === 'DEACTIVATED') {
      return {
        label: 'Reactivate',
        icon: UserCheck,
        onSelect: () => reactivate.mutate({ userId: user.id }),
        disabled: reactivate.isPending,
      }
    }
    if (user.status === 'PENDING') {
      return {
        label: 'Delete invitation',
        icon: Trash2,
        destructive: true,
        onSelect: () => deleteInvited.mutate({ userId: user.id }),
        disabled: deleteInvited.isPending,
      }
    }
    return {
      label: 'Deactivate',
      icon: UserX,
      destructive: true,
      onSelect: () => deactivate.mutate({ userId: user.id }),
      disabled: deactivate.isPending || isSelf,
      disabledReason: isSelf ? 'You can’t deactivate your own account' : undefined,
    }
  }

  const actions: RowAction[] = [
    ...(user.status === 'PENDING'
      ? [
          {
            label: resent ? 'Sent' : 'Resend invitation',
            icon: Mail,
            priority: true,
            onSelect: () => resend.mutate({ userId: user.id }, { onSuccess: () => setResent(true) }),
            disabled: resend.isPending || resent,
          } satisfies RowAction,
        ]
      : []),
    lifecycleAction(),
  ]


  return (
    <TableRow>
      <TableCell>
        <p className="text-sm font-medium">
          {user.name}
          {isSelf ? <span className="ml-1.5 text-xs font-normal text-ink-muted">(you)</span> : null}
        </p>
        <p className="text-xs text-ink-muted">{user.email}</p>
      </TableCell>
      <TableCell>
        <UserStatusBadge status={user.status} />
      </TableCell>
      <TableActionsCell>
        <RowActions label={user.name} actions={actions} />
      </TableActionsCell>
    </TableRow>
  )
}

/**
 * Platform SUPER_ADMIN directory (sidebar: "Users"). List peers and invite more
 * super admins only — not tenant-scoped users.
 */
export function SuperAdminsPage() {
  const usersQuery = useSuperAdmins()
  const [inviting, setInviting] = React.useState(false)

  /**
   * The action that adds to this table, in the table's own header. Hidden while
   * the invite form is open, since the form is the same action already taken.
   */
  function inviteAction(): React.ReactElement | undefined {
    if (inviting) return undefined
    return (
      <Button type="button" variant="ghost" size="sm" onClick={() => setInviting(true)}>
        <UserPlus className="h-4 w-4" /> Invite user
      </Button>
    )
  }

  function renderTable() {
    if (usersQuery.isPending) return <LoadingState label="Loading users…" />
    if (usersQuery.isError) {
      return <ErrorState message={requestErrorMessage(usersQuery.error)} onRetry={() => void usersQuery.refetch()} />
    }
    const users = usersQuery.data.users
    return (
      <Table
        heading={`${String(users.length)} super admin${users.length === 1 ? '' : 's'}`}
        actions={inviteAction()}
      >
        <TableHeader>
          <TableHead>Person</TableHead>
          <TableHead>Status</TableHead>
          <TableActionsHead />
        </TableHeader>
        <TableBody>
          {users.length === 0 ? (
            <TableEmptyRow colSpan={3}>
              No super admins yet. Invite someone to manage the platform.
            </TableEmptyRow>
          ) : null}
          {users.map((user) => (
            <SuperAdminRow key={user.id} user={user} />
          ))}
        </TableBody>
      </Table>
    )
  }

  return (
    <PageContainer className="space-y-6">
      {/* The count moved into the table's own header, where the design system
          keeps a list's label — repeating it here said it twice. */}
      <PageHeader title="Users" description="Invite and manage other platform super admins." />

      {inviting ? <InviteForm onDone={() => setInviting(false)} /> : null}

      {usersQuery.isError && !usersQuery.data ? (
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <AlertCircle className="h-7 w-7 text-danger-ink" />
          <p className="text-sm text-ink-muted">{requestErrorMessage(usersQuery.error)}</p>
          <Button variant="outline" size="sm" onClick={() => void usersQuery.refetch()}>
            Try again
          </Button>
        </div>
      ) : (
        renderTable()
      )}
    </PageContainer>
  )
}
